import { createSlice } from '@reduxjs/toolkit'
import { logoutUser } from '../middlewares/logoutMiddleware'
import { rubricRefinementApi } from '../apis/rubric-refinement.api'
import { RefinementItemTypes } from '../../pages/assignment/components/section-menu/rubric-refinement/chat/constants'

/**
 * Initial state for the rubric refinement slice
 * refinementItems: Array of standardized RefinementItem objects
 * refinementProcess: Process metadata from the API
 * hoveredCategoryId: Currently hovered category ID for UI
 * hasStarted: Whether user clicked 'Let's get started' button
 * processingAction: Whether an action is currently being processed
 * isFinished: Whether the refinement process is complete (item_to_review was null)
 */
const initialState = {
    refinementItems: [],
    refinementProcess: null,
    hoveredCategoryId: null,
    hoveredRefinementItemId: null,
    hasStarted: false,
    processingAction: false, // Track if we're currently processing an action
    isFinished: false, // Track if the refinement process is complete
    isProcessingNextItem: false, // Track if the next queue item is being prepared in the background
}

const getItemIdentity = item => item.queueItemId ?? item.cleaningStepId ?? item.id
const findItemByIdentity = (items, identity) => items.find(item => getItemIdentity(item) === identity)
const getQueueItemIdentity = item => item.queueItemId ?? item.id

const applyReviewedItemId = (state, newReviewedItemId, refinementItemId) => {
    if (!newReviewedItemId) {
        return
    }

    const currentItem = findItemByIdentity(state.refinementItems, refinementItemId)
    if (currentItem) {
        currentItem.cleaningStepId = newReviewedItemId
        currentItem.id = currentItem.cleaningStepId
    }
}

const applyNextItemPayload = (state, payload, refinementItemId = null) => {
    if (payload?.progress) {
        state.refinementProcess.progress = payload.progress
    }

    if (payload?.item === null) {
        state.isFinished = true
        return
    }

    if (payload?.item) {
        const currentItem = refinementItemId
            ? findItemByIdentity(state.refinementItems, refinementItemId)
            : null
        if (currentItem && currentItem.type === RefinementItemTypes.SUBMISSION && refinementItemId) {
            // category selection is applied optimistically before the mutation resolves
        }

        const newItem = payload.item
        if (!state.refinementItems.some(item => getQueueItemIdentity(item) === newItem.queueItemId)) {
            state.refinementItems.push(newItem)
        }
    }
}

const applyMutationResponse = (state, action) => {
    state.processingAction = false

    const refinementItemId = action.meta.arg.originalArgs.refinementItemId

    applyReviewedItemId(state, action.payload?.newReviewedItemId, refinementItemId)

    if (action.payload?.isProcessing) {
        state.isProcessingNextItem = true
        return
    }

    state.isProcessingNextItem = false
    applyNextItemPayload(state, action.payload, refinementItemId)
}

const applyPolledNextItem = (state, payload) => {
    if (payload?.isProcessing) {
        state.isProcessingNextItem = true
        return
    }

    state.isProcessingNextItem = false
    state.processingAction = false
    applyNextItemPayload(state, payload)
}

const rubricRefinementSlice = createSlice({
    name: 'rubricRefinement',
    initialState,
    reducers: {
        setRefinementItems: (state, action) => {
            state.refinementItems = action.payload
        },
        setHoveredCategoryId: (state, action) => {
            state.hoveredCategoryId = action.payload
        },
        setHoveredRefinementItemId: (state, action) => {
            state.hoveredRefinementItemId = action.payload
        },
        selectCategory: (state, action) => {
            const { refinementItemId, categoryId } = action.payload
            const currentItem = findItemByIdentity(state.refinementItems, refinementItemId)
            if (currentItem) {
                currentItem.selectedCategoryId = categoryId
                currentItem.isSkipped = false
                state.processingAction = true // Start processing
            }
        },
        approveDiffReview: (state, action) => {
            const { refinementItemId } = action.payload
            const currentItem = findItemByIdentity(state.refinementItems, refinementItemId)
            if (currentItem) {
                currentItem.diffReviewChoice = 'approved'
                state.processingAction = true // Start processing
            }
        },
        rejectDiffReview: (state, action) => {
            const { refinementItemId } = action.payload
            const currentItem = findItemByIdentity(state.refinementItems, refinementItemId)
            if (currentItem) {
                currentItem.diffReviewChoice = 'rejected'
                state.processingAction = true // Start processing
            }
        },
        skipItem: (state, action) => {
            const { refinementItemId } = action.payload
            const currentItem = findItemByIdentity(state.refinementItems, refinementItemId)
            if (currentItem) {
                currentItem.isSkipped = true
                currentItem.selectedCategoryId = null
                state.processingAction = true // Start processing
                if (currentItem.type === RefinementItemTypes.SUGGESTION) {
                    currentItem.diffReviewChoice = 'rejected'
                }
            }
        },
        revertItemToState: (state, action) => {
            const { refinementItemId, previousState } = action.payload
            const currentItem = findItemByIdentity(state.refinementItems, refinementItemId)
            if (currentItem) {
                currentItem.selectedCategoryId = previousState.selectedCategoryId
                currentItem.isSkipped = previousState.isSkipped
                if (previousState.diffReviewChoice !== undefined) {
                    currentItem.diffReviewChoice = previousState.diffReviewChoice
                }
            }
        },
        startRefinementProcess: state => {
            state.hasStarted = true
        },
        unstartRefinementProcess: state => {
            state.hasStarted = false
        },
        startOverRefinementProcess: () => {
            return { ...initialState, hasStarted: true }
        },
        reset: () => initialState,
    },
    extraReducers: builder => {
        builder
            .addCase(logoutUser, () => initialState)
            // When a new refinement starts, reset the list to contain only the new item
            .addMatcher(rubricRefinementApi.endpoints.startRefinement.matchFulfilled, (state, { payload }) => {
                if (payload) {
                    state.refinementProcess = payload
                    state.isFinished = false
                    state.isProcessingNextItem = !!payload.isProcessing

                    // Initialize refinementItems with reviewedItems if available
                    let initialItems = []

                    // Add reviewed items if they exist
                    if (payload.reviewedItems && payload.reviewedItems.length > 0) {
                        // Process the reviewed items to create new objects with the correct properties
                        initialItems = payload.reviewedItems.map(item => {
                            // Create a new object instead of modifying the existing one
                            const processedItem = { ...item }

                            if (item.type === RefinementItemTypes.SUBMISSION) {
                                if (item.userDecision === 'Skip') {
                                    processedItem.isSkipped = true
                                } else {
                                    processedItem.selectedCategoryId = +item.userDecision
                                }
                            } else if (item.type === RefinementItemTypes.SUGGESTION) {
                                // For suggestions, set the diffReviewChoice based on user decision
                                if (item.userDecision === 'ChangeAccepted') {
                                    processedItem.diffReviewChoice = 'approved'
                                } else {
                                    processedItem.diffReviewChoice = 'rejected'
                                }
                            }
                            return processedItem
                        })
                    }

                    if (initialItems.length) state.hasStarted = true
                    if (payload.item) initialItems = [...initialItems, payload.item]

                    state.refinementItems = initialItems
                }
            })
            // When choosing a category, add the returned item to the list
            .addMatcher(rubricRefinementApi.endpoints.chooseRefinementCategory.matchFulfilled, (state, action) => {
                applyMutationResponse(state, action)

                if (action.payload?.isProcessing) {
                    return
                }

                if (action.payload?.item) {
                    const currentItem = findItemByIdentity(
                        state.refinementItems,
                        action.payload.newReviewedItemId || action.meta.arg.originalArgs.refinementItemId,
                    )
                    if (currentItem && currentItem.type === RefinementItemTypes.SUBMISSION) {
                        currentItem.selectedCategoryId = action.meta.arg.originalArgs.categoryId
                    }
                }
            })
            // When skipping an item, process the returned next item if any
            .addMatcher(rubricRefinementApi.endpoints.skipRefinementItem.matchFulfilled, (state, action) => {
                applyMutationResponse(state, action)

                if (action.payload?.isProcessing) {
                    return
                }

                const currentItem = findItemByIdentity(
                    state.refinementItems,
                    action.payload.newReviewedItemId || action.meta.arg.originalArgs.refinementItemId,
                )
                if (currentItem) {
                    currentItem.isSkipped = true
                    if (currentItem.type === RefinementItemTypes.SUGGESTION) {
                        currentItem.diffReviewChoice = 'rejected'
                    }
                }
            })
            // When confirming a suggestion, process the returned next item if any
            .addMatcher(rubricRefinementApi.endpoints.confirmSuggestion.matchFulfilled, (state, action) => {
                applyMutationResponse(state, action)

                if (action.payload?.isProcessing) {
                    return
                }

                const currentItem = findItemByIdentity(
                    state.refinementItems,
                    action.payload.newReviewedItemId || action.meta.arg.originalArgs.refinementItemId,
                )
                if (currentItem && currentItem.type === RefinementItemTypes.SUGGESTION) {
                    currentItem.diffReviewChoice = 'approved'
                }
            })
            .addMatcher(rubricRefinementApi.endpoints.getNextRefinementItem.matchFulfilled, (state, { payload }) => {
                applyPolledNextItem(state, payload)
            })
            // reset slice on finish
            .addMatcher(rubricRefinementApi.endpoints.finishRefinement.matchFulfilled, () => {
                return initialState
            })
            .addMatcher(rubricRefinementApi.endpoints.abortRefinement.matchFulfilled, state => {
                state.hasStarted = false

                return state
            })
            // Track pending API actions
            .addMatcher(
                action =>
                    action.type.endsWith('/pending') &&
                    (action.type.includes('chooseRefinementCategory') ||
                        action.type.includes('confirmSuggestion') ||
                        action.type.includes('skipRefinementItem')),
                state => {
                    state.processingAction = true
                },
            )
    },
})

export const {
    setRefinementItems,
    setHoveredCategoryId,
    setHoveredRefinementItemId,
    selectCategory,
    approveDiffReview,
    rejectDiffReview,
    skipItem,
    revertItemToState,
    startRefinementProcess,
    unstartRefinementProcess,
    startOverRefinementProcess,
    reset,
} = rubricRefinementSlice.actions

export default rubricRefinementSlice.reducer
